export type AnalyticsMode = "auto" | "off";
export interface SearchAnalyticsEvent { query: string; results: number; locale: string; }
type Callable = (...args: unknown[]) => void;
export interface AnalyticsScope {
  gtag?: Callable; fbq?: Callable; ttq?: { track?: Callable }; dataLayer?: unknown[];
  pys?: { Utils?: { gtagPush?: Callable } };
  smartyfindAnalyticsConsent?: boolean; smartyfindMarketingConsent?: boolean;
  wp_has_consent?: (category: string) => boolean;
  PantofiNoiGDPRDebug?: { consent?: { analytics?: boolean; marketing?: boolean } };
  AeriaConsentV2?: { version?: number | string; cookieName?: string };
  document?: { cookie?: string };
}
export function analyticsMode(value: string | undefined): AnalyticsMode {
  return value?.trim().toLowerCase() === "off" ? "off" : "auto";
}
export function analyticsAllowed(scope: AnalyticsScope, category: "analytics" | "marketing"): boolean {
  const explicit = category === "analytics" ? scope.smartyfindAnalyticsConsent : scope.smartyfindMarketingConsent;
  if (typeof explicit === "boolean") return explicit;
  const pn = scope.PantofiNoiGDPRDebug?.consent?.[category];
  if (typeof pn === "boolean") return pn;
  if (scope.AeriaConsentV2) {
    try {
      const name = scope.AeriaConsentV2.cookieName || "aeria_consent_v2";
      const cookie = scope.document?.cookie?.split("; ").find((part) => part.startsWith(name + "="));
      const data = JSON.parse(decodeURIComponent(cookie?.slice(name.length + 1) || "{}"));
      return Number(data.version) === Number(scope.AeriaConsentV2.version) && data[category] === true;
    } catch { return false; }
  }
  // MerchantPro uses Google's consent commands in its standard data layer.
  // The latest explicit state wins, including revocation after acceptance.
  const consentKey = category === "analytics" ? "analytics_storage" : "ad_storage";
  let googleConsent: boolean | undefined;
  for (const entry of scope.dataLayer ?? []) {
    const command = entry as { [key: number]: unknown } | null;
    if (command?.[0] !== "consent" || !["default", "update"].includes(String(command[1]))) continue;
    const state = (command[2] as Record<string, unknown> | undefined)?.[consentKey];
    if (state === "granted" || state === "denied") googleConsent = state === "granted";
  }
  if (googleConsent !== undefined) return googleConsent;
  try { return scope.wp_has_consent?.(category === "analytics" ? "statistics" : "marketing") === true; }
  catch { return false; }
}
export function analyticsTargets(scope: unknown = globalThis) {
  const host = scope as AnalyticsScope;
  const utils = host.pys?.Utils;
  return {
    gtag: typeof utils?.gtagPush === "function" ? utils.gtagPush.bind(utils) : typeof host.gtag === "function" ? host.gtag : undefined,
    fbq: typeof host.fbq === "function" ? host.fbq : undefined,
    ttq: host.ttq && typeof host.ttq.track === "function" ? host.ttq : undefined,
    dataLayer: Array.isArray(host.dataLayer) ? host.dataLayer : undefined
  };
}
/** Catalog queries only; omit contact details accidentally entered in search. */
export function safeSearchTerm(value: string): string | undefined {
  const term = value.trim().replace(/\s+/g, " ");
  if (!term || /@|https?:\/\/|(?:\d[ .()+-]*){7,}/i.test(term)) return undefined;
  return term.slice(0, 100);
}
export class SearchAnalytics {
  private lastQuery = "";
  private timer: ReturnType<typeof setTimeout> | undefined;
  private pending?: SearchAnalyticsEvent;
  constructor(private readonly mode: AnalyticsMode = "auto", private readonly delayMs = 1200) {}
  trackSearch(event: SearchAnalyticsEvent): void {
    if (this.mode === "off" || !event.query) return;
    this.pending = event;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), this.delayMs);
  }
  flush(scope: unknown = globalThis): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = undefined; }
    const event = this.pending; this.pending = undefined;
    if (!event) return;
    const query = safeSearchTerm(event.query);
    if (!query || query.toLocaleLowerCase() === this.lastQuery) return;
    const sent = this.emit("view_search_results", {
      search_term: query, results_count: event.results, language: event.locale,
      search_status: event.results > 0 ? "results" : "no_results"
    }, "Search", { search_string: query, results_count: event.results }, scope);
    if (sent) this.lastQuery = query.toLocaleLowerCase();
  }
  trackOpen(scope: unknown = globalThis): void {
    this.emit("sf_search_open", {}, "SmartyFindOpen", {}, scope);
  }
  trackProduct(data: { productId: string; productName?: string; query?: string; position?: number }, scope: unknown = globalThis): void {
    this.flush(scope);
    const payload: Record<string, unknown> = { item_id: data.productId, item_name: data.productName?.slice(0, 100), index: data.position };
    const query = safeSearchTerm(data.query || "");
    if (query) payload.search_term = query;
    this.emit("sf_search_product_click", payload, "SmartyFindProductClick", payload, scope);
  }
  private emit(gaName: string, data: Record<string, unknown>, metaName: string, metaData: Record<string, unknown>, scope: unknown): boolean {
    if (this.mode === "off") return false;
    const targets = analyticsTargets(scope);
    const host = scope as AnalyticsScope;
    let sent = false;
    try {
      if (analyticsAllowed(host, "analytics")) {
        const payload = { surface: "smartyfind_widget", search_provider: "smartyfind", ...data };
        if (targets.gtag) { targets.gtag("event", gaName, payload); sent = true; }
        else if (targets.dataLayer) { targets.dataLayer.push({ event: gaName, ...payload }); sent = true; }
      }
    } catch { /* Analytics must not interrupt shopping. */ }
    try {
      if (analyticsAllowed(host, "marketing")) {
        if (targets.fbq) { targets.fbq(metaName === "Search" ? "track" : "trackCustom", metaName, { ...metaData, search_provider: "smartyfind" }); sent = true; }
        if (metaName === "Search" && targets.ttq?.track) targets.ttq.track("Search", { query: metaData.search_string });
      }
    } catch { /* Analytics must not interrupt shopping. */ }
    return sent;
  }
}
